import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────────────────────────────────────
//  API BASE
// ─────────────────────────────────────────────────────────────────────────────
const STAFF_API = "https://schoolcrm-fzcv.onrender.com/api/staff";
const ATT_API   = "https://schoolcrm-fzcv.onrender.com/api/staff-attendance";
const SAL_API   = "https://schoolcrm-fzcv.onrender.com/api/salaries";
const ADV_API   = "https://schoolcrm-fzcv.onrender.com/api/class-advisors";

const STATUS_COLOR = {
  present: "#2E7D32",
  late:    "#F9A825",
  leave:   "#EF6C00",
  absent:  "#C62828",
  none:    "#CFD8DC",
};

function currentMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function getInitials(name) {
  if (!name) return "??";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────────
//  SMALL UI HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function SectionCard({ icon, title, right, children }) {
  return (
    <div style={{ background: "#FFFFFF", borderRadius: 16, padding: 20, marginBottom: 18, boxShadow: "0 1px 4px rgba(13,71,161,0.08)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14, color: "#0D47A1" }}>
          <i className={`fas ${icon}`}></i> {title}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function StatTile({ value, label, color }) {
  return (
    <div style={{ flex: "1 1 100px", background: "#E3F2FD", borderRadius: 12, padding: "12px 10px", textAlign: "center" }}>
      <div style={{ fontWeight: 800, fontSize: 20, color: color || "#0D47A1" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#0D47A1", marginTop: 2 }}>{label}</div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid #E3F2FD" }}>
      <i className={`fas ${icon}`} style={{ color: "#0D47A1", width: 16, marginTop: 2, fontSize: 12 }}></i>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: "#5C7CB8", fontWeight: 600, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 13, color: "#0D2C5C", fontWeight: 600, wordBreak: "break-word" }}>{value || "—"}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function StaffAnalysis() {
  const navigate = useNavigate();

  const [staffList, setStaffList]   = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch]         = useState("");

  const [selected, setSelected]     = useState(null);
  const [month, setMonth]           = useState(currentMonthStr());

  const [advisorClasses, setAdvisorClasses] = useState([]);
  const [attSummary, setAttSummary]         = useState(null);
  const [attRecords, setAttRecords]         = useState([]);
  const [salaryData, setSalaryData]         = useState(null);
  const [detailLoading, setDetailLoading]   = useState(false);
  const [detailError, setDetailError]       = useState("");

  // ── Fetch staff list for sidebar ──
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(STAFF_API);
        const data = await r.json();
        const list = Array.isArray(data) ? data : [];
        setStaffList(list);
        if (list.length) setSelected(list[0]);
      } catch {
        setStaffList([]);
      } finally {
        setListLoading(false);
      }
    })();
  }, []);

  // ── Fetch full analysis for selected staff + month ──
  useEffect(() => {
    if (!selected?._id) return;
    (async () => {
      setDetailLoading(true);
      setDetailError("");
      try {
        const [year, mm] = month.split("-").map(Number);

        const [advRes, sumRes, monthRes, salRes] = await Promise.allSettled([
          fetch(`${ADV_API}/staff/${selected._id}/classes`),
          fetch(`${ATT_API}/summary/${selected._id}`),
          fetch(`${ATT_API}/month?year=${year}&month=${mm}&staffId=${selected._id}`),
          fetch(`${SAL_API}/employee/${selected._id}/attendance-salary?month=${month}&type=Staff`),
        ]);

        setAdvisorClasses(advRes.status === "fulfilled" && advRes.value.ok ? await advRes.value.json() : []);
        setAttSummary(sumRes.status === "fulfilled" && sumRes.value.ok ? await sumRes.value.json() : null);
        setAttRecords(monthRes.status === "fulfilled" && monthRes.value.ok ? await monthRes.value.json() : []);
        setSalaryData(salRes.status === "fulfilled" && salRes.value.ok ? await salRes.value.json() : null);
      } catch (err) {
        setDetailError("Could not load full analysis. Is the backend running?");
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [selected, month]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staffList;
    return staffList.filter(s =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.dept || "").toLowerCase().includes(q) ||
      (s.role || "").toLowerCase().includes(q)
    );
  }, [staffList, search]);

  // ── Attendance chart data (days of selected month) ──
  const daysInMonth = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m, 0).getDate();
  }, [month]);

  const dayStatusMap = useMemo(() => {
    const map = {};
    attRecords.forEach(r => {
      const day = Number(r.date.split("-")[2]);
      map[day] = r.status;
    });
    return map;
  }, [attRecords]);

  const attendancePct = attSummary && attSummary.totalDays
    ? Math.round(((attSummary.present + attSummary.late) / attSummary.totalDays) * 100)
    : null;

  const perfScore = attendancePct === null ? null : Math.max(0, Math.min(100, attendancePct));
  const perfLabel = perfScore === null ? "—"
    : perfScore >= 90 ? "Excellent"
    : perfScore >= 75 ? "Good"
    : perfScore >= 60 ? "Average"
    : "Needs Improvement";
  const perfColor = perfScore === null ? "#5C7CB8"
    : perfScore >= 90 ? "#2E7D32"
    : perfScore >= 75 ? "#1565C0"
    : perfScore >= 60 ? "#F9A825"
    : "#C62828";

  return (
    <div style={{ fontFamily: "'Poppins',sans-serif", background: "#F4F8FF", minHeight: "100vh" }}>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />

      {/* ── Header ── */}
      <div style={{ background: "linear-gradient(135deg,#0D47A1,#1565C0)", padding: "16px 24px", display: "flex", alignItems: "center", gap: 14, color: "#FFFFFF", boxShadow: "0 2px 10px rgba(13,71,161,0.25)" }}>
        <button onClick={() => navigate("/admin")}
          style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#FFFFFF", width: 38, height: 38, borderRadius: 10, cursor: "pointer" }}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 8 }}>
            <i className="fas fa-chart-line"></i> Staff Analysis &amp; Performance
          </div>
          <div style={{ fontSize: 12, color: "#BBD8FF" }}>Full profile, attendance, salary &amp; performance — per staff</div>
        </div>
      </div>

      {/* ── Body: Sidebar + Detail ── */}
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", minHeight: "calc(100vh - 70px)" }} className="sa-body-grid">
        {/* ── Sidebar ── */}
        <div style={{ background: "#FFFFFF", borderRight: "1px solid #E3F2FD", padding: 16, overflowY: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F4F8FF", border: "1px solid #E3F2FD", borderRadius: 10, padding: "8px 12px", marginBottom: 14 }}>
            <i className="fas fa-search" style={{ color: "#5C7CB8", fontSize: 12 }}></i>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff..."
              style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, width: "100%", fontFamily: "'Poppins',sans-serif", color: "#0D2C5C" }}
            />
          </div>

          {listLoading ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#0D47A1" }}>
              <i className="fas fa-spinner fa-pulse" style={{ fontSize: 20 }}></i>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#5C7CB8", fontSize: 12 }}>No staff found</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {filteredStaff.map((s) => {
                const active = selected?._id === s._id;
                return (
                  <div key={s._id} onClick={() => setSelected(s)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 10,
                      cursor: "pointer", background: active ? "#0D47A1" : "transparent",
                      transition: "background .15s",
                    }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                      background: active ? "#FFFFFF" : "#E3F2FD",
                      color: active ? "#0D47A1" : "#0D47A1",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: 12,
                    }}>
                      {getInitials(s.name)}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: active ? "#FFFFFF" : "#0D2C5C", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {s.name}
                      </div>
                      <div style={{ fontSize: 10, color: active ? "#BBD8FF" : "#5C7CB8" }}>
                        {s.dept} {s.role ? `• ${s.role}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Detail Panel ── */}
        <div style={{ padding: 22, overflowY: "auto" }}>
          {!selected ? (
            <div style={{ textAlign: "center", padding: "80px 0", color: "#5C7CB8" }}>
              <i className="fas fa-user-tie" style={{ fontSize: 32, marginBottom: 10 }}></i>
              <div>Select a staff member from the left to view full analysis.</div>
            </div>
          ) : (
            <>
              {/* Profile header */}
              <div style={{ background: "linear-gradient(135deg,#0D47A1,#1565C0)", borderRadius: 16, padding: 22, color: "#FFFFFF", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 18, marginBottom: 18 }}>
                <div style={{ width: 64, height: 64, borderRadius: 18, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 24, flexShrink: 0 }}>
                  {getInitials(selected.name)}
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 700, fontSize: 20 }}>{selected.name}</div>
                  <div style={{ fontSize: 12, color: "#BBD8FF", marginTop: 2 }}>
                    {selected.role || "Staff"} — {selected.dept || "—"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{selected.exp || "—"}</div>
                    <div style={{ fontSize: 10, color: "#BBD8FF" }}>Experience</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{selected.qual || "—"}</div>
                    <div style={{ fontSize: 10, color: "#BBD8FF" }}>Qualification</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>₹{selected.salary || 0}</div>
                    <div style={{ fontSize: 10, color: "#BBD8FF" }}>Base Salary</div>
                  </div>
                </div>
              </div>

              {detailError && (
                <div style={{ background: "#FFEBEE", color: "#C62828", borderRadius: 10, padding: "10px 14px", fontSize: 12, fontWeight: 600, marginBottom: 16 }}>
                  <i className="fas fa-exclamation-circle"></i> {detailError}
                </div>
              )}

              {/* A-Z Profile Details */}
              <SectionCard icon="fa-id-card" title="Profile Details (A – Z)">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0 24px" }}>
                  <InfoRow icon="fa-user" label="Full Name" value={selected.name} />
                  <InfoRow icon="fa-user-tag" label="Username" value={selected.username} />
                  <InfoRow icon="fa-briefcase" label="Role" value={selected.role} />
                  <InfoRow icon="fa-building" label="Department" value={selected.dept} />
                  <InfoRow icon="fa-phone" label="Phone" value={selected.phone} />
                  <InfoRow icon="fa-envelope" label="Email" value={selected.email} />
                  <InfoRow icon="fa-graduation-cap" label="Qualification" value={selected.qual} />
                  <InfoRow icon="fa-clock" label="Experience" value={selected.exp} />
                  <InfoRow icon="fa-calendar-plus" label="Date of Joining" value={selected.doj || selected.join} />
                  <InfoRow icon="fa-money-bill-wave" label="Base Salary" value={selected.salary ? `₹${selected.salary}` : "—"} />
                </div>
                {Array.isArray(selected.subjects) && selected.subjects.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 10, color: "#5C7CB8", fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>Subjects Handling</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {selected.subjects.map((sub) => (
                        <span key={sub} style={{ background: "#E3F2FD", color: "#0D47A1", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 20 }}>{sub}</span>
                      ))}
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* Class Advisor */}
              <SectionCard icon="fa-chalkboard-teacher" title="Class Advisor / Maintenance">
                {detailLoading ? (
                  <div style={{ color: "#5C7CB8", fontSize: 12 }}><i className="fas fa-spinner fa-pulse"></i> Loading...</div>
                ) : advisorClasses.length === 0 ? (
                  <div style={{ color: "#5C7CB8", fontSize: 13 }}>Not currently assigned as Class Advisor for any class.</div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {advisorClasses.map((c) => (
                      <span key={c._id || c.className} style={{ background: "#0D47A1", color: "#FFFFFF", fontSize: 12, fontWeight: 600, padding: "6px 14px", borderRadius: 20, display: "flex", alignItems: "center", gap: 6 }}>
                        <i className="fas fa-chalkboard"></i> {c.className}
                      </span>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* Attendance */}
              <SectionCard
                icon="fa-calendar-check"
                title="Attendance"
                right={
                  <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                    style={{ border: "1px solid #E3F2FD", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "'Poppins',sans-serif", color: "#0D47A1" }} />
                }
              >
                {detailLoading ? (
                  <div style={{ color: "#5C7CB8", fontSize: 12 }}><i className="fas fa-spinner fa-pulse"></i> Loading attendance...</div>
                ) : (
                  <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
                      <StatTile value={attSummary?.present ?? 0} label="Present" color="#2E7D32" />
                      <StatTile value={attSummary?.late ?? 0} label="Late" color="#F9A825" />
                      <StatTile value={attSummary?.leave ?? 0} label="Leave" color="#EF6C00" />
                      <StatTile value={attSummary?.absent ?? 0} label="Absent" color="#C62828" />
                      <StatTile value={attendancePct !== null ? `${attendancePct}%` : "—"} label="Attendance Rate" />
                    </div>

                    {/* Bar chart — one bar per day of selected month */}
                    <div style={{ overflowX: "auto" }}>
                      <svg width={Math.max(daysInMonth * 18, 320)} height="110" style={{ display: "block" }}>
                        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                          const status = dayStatusMap[day] || "none";
                          const heightMap = { present: 80, late: 62, leave: 44, absent: 26, none: 10 };
                          const h = heightMap[status];
                          const x = (day - 1) * 18;
                          return (
                            <g key={day}>
                              <rect x={x + 3} y={90 - h} width={11} height={h} rx={3}
                                fill={STATUS_COLOR[status]} opacity={status === "none" ? 0.5 : 1}>
                                <title>{`Day ${day}: ${status}`}</title>
                              </rect>
                              <text x={x + 9} y={104} fontSize="8" textAnchor="middle" fill="#5C7CB8">{day}</text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                    <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
                      {Object.entries({ present: "Present", late: "Late", leave: "Leave", absent: "Absent", none: "No Record" }).map(([k, label]) => (
                        <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#5C7CB8" }}>
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: STATUS_COLOR[k], display: "inline-block" }}></span> {label}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </SectionCard>

              {/* Salary */}
              <SectionCard icon="fa-wallet" title={`Salary — ${month}`}>
                {detailLoading ? (
                  <div style={{ color: "#5C7CB8", fontSize: 12 }}><i className="fas fa-spinner fa-pulse"></i> Loading salary...</div>
                ) : !salaryData ? (
                  <div style={{ color: "#5C7CB8", fontSize: 13 }}>No salary data available for this month.</div>
                ) : (
                  <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
                      <StatTile value={`₹${salaryData.baseSalary}`} label="Base Salary" />
                      <StatTile value={`₹${salaryData.calculatedSalary}`} label="Payable This Month" color="#2E7D32" />
                      <StatTile value={`₹${salaryData.deductions?.totalDeductions ?? 0}`} label="Total Deductions" color="#C62828" />
                      <StatTile value={`₹${salaryData.dailySalary}`} label="Per Day" />
                    </div>

                    {/* base vs payable bar comparison */}
                    <div style={{ marginBottom: 16 }}>
                      {[
                        { label: "Base Salary", value: salaryData.baseSalary, color: "#90CAF9" },
                        { label: "Payable", value: salaryData.calculatedSalary, color: "#0D47A1" },
                      ].map((row) => {
                        const max = Math.max(salaryData.baseSalary, 1);
                        const pct = Math.round((row.value / max) * 100);
                        return (
                          <div key={row.label} style={{ marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#0D47A1", marginBottom: 4 }}>
                              <span>{row.label}</span><span>₹{row.value}</span>
                            </div>
                            <div style={{ background: "#E3F2FD", borderRadius: 8, height: 10, overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: row.color, borderRadius: 8 }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ fontSize: 10, color: "#5C7CB8", fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>Deduction Breakdown</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                      <InfoRow icon="fa-hourglass-half" label="Late Days" value={`${salaryData.deductions?.lateCount ?? 0} (₹${salaryData.deductions?.lateDeduction ?? 0})`} />
                      <InfoRow icon="fa-plane-departure" label="Leave Days" value={`${salaryData.deductions?.leaveCount ?? 0} (₹${salaryData.deductions?.leaveDeduction ?? 0})`} />
                      <InfoRow icon="fa-user-times" label="Absent Days" value={`${salaryData.deductions?.absentCount ?? 0} (₹${salaryData.deductions?.absentDeduction ?? 0})`} />
                    </div>
                  </>
                )}
              </SectionCard>

              {/* Performance */}
              <SectionCard icon="fa-chart-pie" title="Performance Overview">
                <div style={{ fontSize: 11, color: "#5C7CB8", marginBottom: 14 }}>
                  Calculated from this month's attendance rate (present + late ÷ total working days).
                </div>
                {perfScore === null ? (
                  <div style={{ color: "#5C7CB8", fontSize: 13 }}>Not enough attendance data yet.</div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                    <div style={{ position: "relative", width: 110, height: 110, flexShrink: 0 }}>
                      <svg width="110" height="110" viewBox="0 0 110 110">
                        <circle cx="55" cy="55" r="46" fill="none" stroke="#E3F2FD" strokeWidth="12" />
                        <circle cx="55" cy="55" r="46" fill="none" stroke={perfColor} strokeWidth="12"
                          strokeDasharray={`${(perfScore / 100) * 289} 289`} strokeLinecap="round"
                          transform="rotate(-90 55 55)" />
                      </svg>
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                        <div style={{ fontWeight: 800, fontSize: 20, color: perfColor }}>{perfScore}%</div>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: perfColor }}>{perfLabel}</div>
                      <div style={{ fontSize: 12, color: "#5C7CB8", marginTop: 4 }}>
                        {attSummary?.present ?? 0} present + {attSummary?.late ?? 0} late out of {attSummary?.totalDays ?? 0} working days this month.
                      </div>
                    </div>
                  </div>
                )}
              </SectionCard>
            </>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .sa-body-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}